/**
 * Kontora BFF – Onboarding State (client-side)
 *
 * Persistiert Tour- und Workflow-Fortschritt im LocalStorage.
 *
 * Bewusst KEINE DB-Persistierung:
 * - Onboarding ist geräte- und sessionspezifisch.
 * - LocalStorage wird beim Logout sowieso gelöscht (oder persistiert gerätespezifisch).
 * - Multi-Device-Sync kann später via `user_onboarding_state` ergänzt werden.
 *
 * Schema:
 *   kontora_onboarding_v1
 *   {
 *     welcomeDismissed: boolean,
 *     experienced: boolean,
 *     completedTours: string[],
 *     completedWorkflows: string[],
 *     skippedTours: string[],
 *     updatedAt: ISO-String,
 *   }
 */

import { useCallback } from 'react';
import { useBrowserState } from '../use-browser-state';

export interface OnboardingState {
  welcomeDismissed: boolean;
  experienced: boolean;
  completedTours: string[];
  completedWorkflows: string[];
  skippedTours: string[];
  updatedAt: string;
}

const STORAGE_KEY = 'kontora_onboarding_v1';

const DEFAULT_STATE: OnboardingState = {
  welcomeDismissed: false,
  experienced: false,
  completedTours: [],
  completedWorkflows: [],
  skippedTours: [],
  updatedAt: new Date(0).toISOString(),
};

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/**
 * Pure-Function: liest und normalisiert einen rohen JSON-String zu OnboardingState.
 * Wird sowohl vom Hook als auch in Unit-Tests verwendet.
 */
export function parseOnboardingState(raw: string | null | undefined): OnboardingState {
  if (!raw) return { ...DEFAULT_STATE };
  try {
    const parsed = JSON.parse(raw) as Partial<OnboardingState>;
    return {
      welcomeDismissed: parsed.welcomeDismissed === true,
      experienced: parsed.experienced === true,
      completedTours: Array.isArray(parsed.completedTours)
        ? (parsed.completedTours as unknown[]).filter((x): x is string => typeof x === 'string')
        : [],
      completedWorkflows: Array.isArray(parsed.completedWorkflows)
        ? (parsed.completedWorkflows as unknown[]).filter((x): x is string => typeof x === 'string')
        : [],
      skippedTours: Array.isArray(parsed.skippedTours)
        ? (parsed.skippedTours as unknown[]).filter((x): x is string => typeof x === 'string')
        : [],
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date(0).toISOString(),
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function readState(): OnboardingState {
  if (!isBrowser()) return { ...DEFAULT_STATE };
  return parseOnboardingState(window.localStorage.getItem(STORAGE_KEY));
}

function isQuotaError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const name = error.name;
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
  return error.message.toLowerCase().includes('quota');
}

export function writeState(state: OnboardingState): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    if (!isQuotaError(error) && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[onboarding-state] failed to persist state', error);
    }
    if (isQuotaError(error) && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[onboarding-state] localStorage quota exceeded, state not persisted', error);
    }
  }
}

/**
 * Hook für Onboarding-State. Liest beim Mount, schreibt bei Updates.
 *
 * Hinweis: SSR-sicher. Während SSR liefert der Hook den Default-State,
 * erst nach Hydration den tatsächlichen Wert. Für UI-Checks (Modal zeigen)
 * daher den `isReady`-Wert beachten.
 */
export function useOnboardingState() {
  const [state, setState, isReady] = useBrowserState(readState, DEFAULT_STATE);

  const update = useCallback((patch: Partial<OnboardingState>) => {
    setState((prev) => {
      const next: OnboardingState = {
        ...prev,
        ...patch,
        updatedAt: new Date().toISOString(),
      };
      writeState(next);
      return next;
    });
  }, [setState]);

  const reset = useCallback(() => {
    const fresh: OnboardingState = {
      ...DEFAULT_STATE,
      updatedAt: new Date().toISOString(),
    };
    writeState(fresh);
    setState(fresh);
  }, [setState]);

  const dismissWelcome = useCallback(() => {
    update({ welcomeDismissed: true });
  }, [update]);

  const resetWelcome = useCallback(() => {
    update({ welcomeDismissed: false });
  }, [update]);

  const completeTour = useCallback(
    (tourId: string) => {
      setState((prev) => {
        if (prev.completedTours.includes(tourId)) return prev;
        const next: OnboardingState = {
          ...prev,
          completedTours: [...prev.completedTours, tourId],
          skippedTours: prev.skippedTours.filter((id) => id !== tourId),
          updatedAt: new Date().toISOString(),
        };
        writeState(next);
        return next;
      });
    },
    [setState],
  );

  const skipTour = useCallback((tourId: string) => {
    setState((prev) => {
      if (prev.skippedTours.includes(tourId) || prev.completedTours.includes(tourId)) return prev;
      const next: OnboardingState = {
        ...prev,
        skippedTours: [...prev.skippedTours, tourId],
        updatedAt: new Date().toISOString(),
      };
      writeState(next);
      return next;
    });
  }, [setState]);

  const setExperienced = useCallback(
    (value: boolean) => {
      update({ experienced: value });
    },
    [update],
  );

  const completeWorkflow = useCallback((workflowCode: string) => {
    setState((prev) => {
      if (prev.completedWorkflows.includes(workflowCode)) return prev;
      const next: OnboardingState = {
        ...prev,
        completedWorkflows: [...prev.completedWorkflows, workflowCode],
        updatedAt: new Date().toISOString(),
      };
      writeState(next);
      return next;
    });
  }, [setState]);

  return {
    state,
    isReady,
    isFirstVisit: isReady && !state.welcomeDismissed,
    shouldShowTours: isReady && !state.experienced,
    dismissWelcome,
    resetWelcome,
    completeTour,
    skipTour,
    completeWorkflow,
    setExperienced,
    reset,
  };
}
